import type { ResolvableTitleTemplate, Link, Meta, Script, UseHeadInput, ResolvableTitle } from '@unhead/vue/types'
import type { IRogue } from '@/js/services/RogueTypes'

function getHREFLangTags(related: {[key: string] : string} = {}) {
  const alternates: {[key: string] : string} = {}

  Object.entries(related).forEach(([store, path]) => {
    if (path.indexOf('http') === 0) {
      path = path.replace(/https:\/\/www\..+?\//, '')
    }
    if (path.indexOf('/') !== 0) {
      path = `/${path}`
    }
    if (path.indexOf('?') !== -1) {
      path = path.split('?')[0]
    }
    // Every page except a site's root should NOT end on a trailing slash. A root path
    // ('/') is left as-is, so it stays on the domain/locale link itself
    // (e.g. https://www.roguefitness.com/gb/) instead of stripping down to no slash.
    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1)
    }

    if (store === 'US') {
      alternates['x-default'] = `https://www.roguefitness.com${path}`
      alternates['en-US'] = `https://www.roguefitness.com${path}`
    } else if (store === 'CANADA') {
      alternates['en-CA'] = `https://www.roguecanada.ca${path}`
    } else if (store === 'AUSTRALIA') {
      alternates['en-AU'] = `https://www.rogueaustralia.com.au${path}`
    } else if (store === 'EUROPE') {
      alternates['en-GB'] = `https://www.roguefitness.com/gb${path}`
      alternates['es-ES'] = `https://www.roguefitness.com/es${path}`
      alternates['en-ES'] = `https://www.roguefitness.com/es/en${path}`
      alternates['fr-FR'] = `https://www.roguefitness.com/fr${path}`
      alternates['en-FR'] = `https://www.roguefitness.com/fr/en${path}`
      alternates['nl-BE'] = `https://www.roguefitness.com/be${path}`
      alternates['en-BE'] = `https://www.roguefitness.com/be/en${path}`
      alternates['de-DE'] = `https://www.roguefitness.com/de${path}`
      alternates['en-DE'] = `https://www.roguefitness.com/de/en${path}`
    } else if (store === 'UK') {
      alternates['en-GB'] = `https://www.roguefitness.com/gb${path}`
    }
  })

  return alternates
}

export type MetadataType = {
  url?: string,
  robots?: 'noindex' | 'all' | 'noindex,nofollow' | 'none' | 'nofollow',
  title?: ResolvableTitle,
  locale?: string,

  image?: string,
  imageWidth?: number,
  imageHeight?: number,
  imageAlt?: string,
  keywords?: string,
  description?: string,

  meta?: Meta[],
  link?: Link[],
  script?: Script[],

  // pagination
  next?: string,
  prev?: string,

  // OG tags
  type?: 'website' | 'article' | 'product',
  price?: number,
  currency?: string,

  titleTemplate?: ResolvableTitleTemplate,
}

export default function metadata(head: MetadataType, rogue: IRogue): UseHeadInput {
  const link: Link[] = head.link || []
  const meta: Meta[] = []
  const script: Script[] = []

  const obj: UseHeadInput = { meta, link, script }

  if (!rogue.l10n.index || (Array.isArray(rogue.l10n.index) && !rogue.l10n.index.find(lang => lang === rogue.l10n.lang))) {
    // We dont want to index most pages on APO, but we do want to index the homepage
    if (rogue.site.code === 'APO' && rogue.router?.currentRoute.value.path === '/') {
      head.robots = 'all'
    } else {
      head.robots = 'noindex,nofollow'
    }
  }

  if (head.script) {
    head.script.forEach(s => script.push(s))
  }

  if (head.robots === 'none') {
    head.robots = 'noindex,nofollow'
  }

  if (head.robots) {
    meta.push({ name: 'robots', content: head.robots, key: 'robots' })
  }

  if (head.url && head.robots !== 'noindex' && head.robots !== 'noindex,nofollow') {
    let href = head.url
    if (rogue.l10n?.path && !href.startsWith('http') && !href.startsWith(rogue.l10n.path)) {
      href = rogue.l10n.path + href
    }

    // A site's root — the bare "/" or a locale root like /gb/ — is a directory, not a
    // page, and keeps its trailing slash. Every other page should NOT have one.
    const hrefPath = href.startsWith('http') ? new URL(href).pathname : href
    const isRootPath = hrefPath === (rogue.l10n.path ? rogue.l10n.path + '/' : '/')

    if (!isRootPath && href.endsWith('/')) {
      href = href.substring(0, href.length - 1)
    }

    // Make sure we dont accidentally double up the slash
    if (href.indexOf('.com//') !== -1) {
      href = href.replace('.com//', '.com/')
    }

    link.push({ rel: 'canonical', href, key: 'canonical' })
    meta.push({ property: 'og:url', content: href, key: 'og:url' })
  }

  if (head.next) {
    link.push({ rel: 'next', href: head.next, key: 'next' })
  }

  if (head.prev) {
    link.push({ rel: 'prev', href: head.prev, key: 'prev' })
  }

  // If someone explicitly sets the alternate links, then we won't set them here
  if (!link.find(l => l.rel === 'alternate')) {
    let related = rogue.routeResp?.related

    // If we did not have to load a route, that means we are in a SPA route and can add hreflang tags
    if (!rogue.routeResp) {
      related = {
        US        : rogue.router?.currentRoute.value.fullPath,
        EUROPE    : rogue.router?.currentRoute.value.fullPath,
        CANADA    : rogue.router?.currentRoute.value.fullPath,
        AUSTRALIA : rogue.router?.currentRoute.value.fullPath,
        UK        : rogue.router?.currentRoute.value.fullPath,
      }
    }

    if(rogue.site.code !== 'APO') {

      const relatedRoutes = getHREFLangTags(related)

      link.push(...Object.entries(relatedRoutes)
        .map(([lang, route]) => ({
          rel      : 'alternate',
          hreflang : lang,
          href     : route,
          key      : `hreflang-${lang}`,
        })))
    }
  }

  if (head.title) {
    obj.title = head.title
    if (typeof head.title === 'string') {
      meta.push({ property: 'itemprop:name', content: head.title, key: 'itemprop:name' })
      meta.push({ property: 'og:title', content: head.title, key: 'og:title' })
    }
  }

  if (rogue.l10n.locale) {
    meta.push({ property: 'og:locale', content: rogue.l10n.locale, key: 'og:locale' })
  }

  if (head.titleTemplate) {
    obj.titleTemplate = head.titleTemplate
  }

  if (head.type) {
    meta.push({ property: 'og:type', content: head.type, key: 'og:type' })
  }

  if (head.meta) {
    meta.push(...head.meta)
  }

  if (head.description) {
    const content = head.description.replace(/<\/?[^>]+(>|$)/g, '')
    meta.push(
      { name: 'description', content, key: 'description' },
      { property: 'og:description', content, key: 'og:description' }
    )
  }

  if (head.image) {
    meta.push({ property: 'og:image', content: head.image, key: 'og:image' })
    if (head.imageWidth) {
      meta.push({ property: 'og:image:width', content: String(head.imageWidth), key: 'og:image:width' })
    }
    if (head.imageHeight) {
      meta.push({ property: 'og:image:height', content: String(head.imageHeight), key: 'og:image:height' })
    }
    if (head.imageAlt) {
      meta.push({ property: 'og:image:alt', content: head.imageAlt, key: 'og:image:alt' })
    }
    meta.push({ name: 'twitter:card', content: 'summary_large_image', key: 'twitter:card' })
    meta.push({ name: 'twitter:image', content: head.image, key: 'twitter:image' })
  }

  return obj
}
